import { useMemo, useRef, useState, type KeyboardEvent } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { Package, Truck, X } from 'lucide-react'

import { Button } from '../../radix'
import {
  calendarDayLabel,
  calendarWeeks,
  formatMonthRange,
  formatMonthShort,
  formatShortDate,
  type CalendarDay,
  type FulfillmentType,
} from '../../../utils/fulfillmentDates'

// The mock's "Earliest" tag (DEV-51011 AC1). Not InStockBadge: that one is
// uppercase and borderless by design, matching the search-card badges.
// leading-4 and rounded-full reproduce the mock's 22px-high pill; without them
// it inherits text-sm's 20px line height here and renders 26px high.
const EARLIEST_TAG_CLASS =
  'rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold leading-4 text-emerald-700'

const WEEKDAY_HEADERS = [
  ['M', 'Monday'],
  ['T', 'Tuesday'],
  ['W', 'Wednesday'],
  ['T', 'Thursday'],
  ['F', 'Friday'],
  ['S', 'Saturday'],
  ['S', 'Sunday'],
] as const

type FulfillmentMethod = 'collection' | 'delivery'

function methodFor(type: FulfillmentType): FulfillmentMethod {
  return type === 'pickup' ? 'collection' : 'delivery'
}

interface FulfillmentDateRowProps {
  fulfillmentType: FulfillmentType
  /** Day the order is booked for; null only when there is no date to show at all. */
  bookedDate: string | null
  /** The order's earliest bookable day — where the choosable window starts. */
  earliestDate: string
  /** Last day the sheet offers (7 days on, or the supplier's cap). */
  lastChoosableDate: string
  /** False when a line has no supplier date: the branch confirms the day (5.9). */
  isConfirmed: boolean
  /** False when the supplier can't take a date (AC13) or there's no other day. */
  canChange: boolean
  /** Live stock (and so the line dates) hasn't arrived yet. */
  isLoading: boolean
  onChange: (date: string) => void
}

/**
 * "Collection date  Fri 18 Sept [Earliest]  Change" in the checkout's pickup /
 * delivery block (DEV-51011 AC1, AC6, AC13).
 */
export function FulfillmentDateRow({
  fulfillmentType,
  bookedDate,
  earliestDate,
  lastChoosableDate,
  isConfirmed,
  canChange,
  isLoading,
  onChange,
}: FulfillmentDateRowProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const method = methodFor(fulfillmentType)
  const Icon = fulfillmentType === 'pickup' ? Package : Truck
  const showEarliestTag = isConfirmed && bookedDate !== null && bookedDate === earliestDate

  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2">
      <Icon className="h-5 w-5 flex-shrink-0 text-gray-500" aria-hidden="true" />
      <div className="min-w-0 flex-1 py-1">
        <p className="text-xs text-gray-500">
          {method === 'collection' ? 'Collection date' : 'Delivery date'}
        </p>
        {isLoading ? (
          <p className="text-sm text-gray-400">Checking availability…</p>
        ) : bookedDate !== null ? (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-bold text-gray-900">
            <span className="whitespace-nowrap">{formatShortDate(bookedDate)}</span>
            {showEarliestTag && <span className={EARLIEST_TAG_CLASS}>Earliest</span>}
          </p>
        ) : null}
        {!isLoading && !isConfirmed && (
          <p
            className={clsx(
              bookedDate !== null
                ? 'mt-0.5 text-xs text-gray-500'
                : 'text-sm font-bold text-gray-900',
            )}
          >
            To be confirmed by the branch
          </p>
        )}
      </div>
      {canChange && !isLoading && bookedDate !== null && (
        <Dialog.Root open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <Dialog.Trigger asChild>
            <button
              type="button"
              aria-label={`Change ${method} date`}
              className="-mr-1 min-h-11 flex-shrink-0 rounded-2xl px-3 text-sm font-semibold text-gray-900 underline underline-offset-2 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            >
              Change
            </button>
          </Dialog.Trigger>
          {/* Mounted only while open, so an unconfirmed pick is thrown away on
              close (AC7) and the next open starts from the booked date. */}
          {isSheetOpen && (
            <FulfillmentDateSheet
              method={method}
              earliestDate={earliestDate}
              lastChoosableDate={lastChoosableDate}
              isConfirmed={isConfirmed}
              selectedDate={bookedDate}
              onConfirm={(date) => {
                onChange(date)
                setIsSheetOpen(false)
              }}
            />
          )}
        </Dialog.Root>
      )}
    </div>
  )
}

interface FulfillmentDateSheetProps {
  method: FulfillmentMethod
  earliestDate: string
  lastChoosableDate: string
  isConfirmed: boolean
  selectedDate: string
  onConfirm: (date: string) => void
}

/**
 * Bottom-sheet calendar (DEV-51011 AC3–AC5, AC7, AC8, AC10, AC11). Same
 * shell as SaveToListSheet: Radix traps focus, closes on Escape / backdrop /
 * Close and hands focus back to "Change"; the slide-up is skipped under
 * reduced motion (animate-sheet-enter).
 */
function FulfillmentDateSheet({
  method,
  earliestDate,
  lastChoosableDate,
  isConfirmed,
  selectedDate,
  onConfirm,
}: FulfillmentDateSheetProps) {
  const weeks = useMemo(
    () => calendarWeeks(earliestDate, lastChoosableDate),
    [earliestDate, lastChoosableDate],
  )
  const days = useMemo(() => weeks.flat(), [weeks])
  const [pendingDate, setPendingDate] = useState(selectedDate)
  const [focusedDate, setFocusedDate] = useState(selectedDate)
  const dayRefs = useRef(new Map<string, HTMLButtonElement>())

  const onGridKeyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    const index = days.findIndex((day) => day.date === focusedDate)
    if (index === -1) return
    const target = nextFocusableDay(days, index, event.key)
    if (target === undefined) return
    event.preventDefault()
    if (target === index) return
    setFocusedDate(days[target].date)
    dayRefs.current.get(days[target].date)?.focus()
  }

  return (
    <Dialog.Portal>
      <Dialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/50" />
      <Dialog.Content
        onOpenAutoFocus={(event) => {
          // AC3: focus lands on the selected day, not the Close button.
          event.preventDefault()
          dayRefs.current.get(selectedDate)?.focus()
        }}
        className="animate-sheet-enter fixed inset-x-0 bottom-0 z-[51] mx-auto flex max-h-[85dvh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-[0_-10px_28px_rgba(32,26,46,0.2)] focus:outline-none"
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-gray-300" aria-hidden="true" />
        <div className="flex items-center gap-2 px-4 pb-1 pt-1">
          <Dialog.Title className="min-w-0 flex-1 text-lg font-bold text-gray-900">
            Choose {method} date
          </Dialog.Title>
          <Dialog.Close
            aria-label="Close"
            className="-mr-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </Dialog.Close>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-3">
          <table className="w-full table-fixed border-collapse" onKeyDown={onGridKeyDown}>
            <caption className="px-1 pb-2 text-left text-sm font-semibold text-gray-700">
              {formatMonthRange(days[0].date, days[days.length - 1].date)}
            </caption>
            <thead>
              <tr>
                {WEEKDAY_HEADERS.map(([short, long]) => (
                  <th key={long} scope="col" className="p-0 pb-1 text-center text-xs font-semibold text-gray-500">
                    <span aria-hidden="true">{short}</span>
                    <span className="sr-only">{long}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week[0].date}>
                  {week.map((day) => {
                    const isAvailable = day.status === 'available'
                    const isSelected = day.date === pendingDate
                    const dayOfMonth = Number(day.date.slice(8))
                    return (
                      <td key={day.date} className="px-0.5 py-1 text-center">
                        <button
                          ref={(element) => {
                            if (element) dayRefs.current.set(day.date, element)
                            else dayRefs.current.delete(day.date)
                          }}
                          type="button"
                          disabled={!isAvailable}
                          aria-pressed={isAvailable ? isSelected : undefined}
                          aria-label={calendarDayLabel(day, isConfirmed)}
                          tabIndex={day.date === focusedDate ? 0 : -1}
                          onClick={() => {
                            setPendingDate(day.date)
                            setFocusedDate(day.date)
                          }}
                          className={clsx(
                            'relative mx-auto flex h-12 w-full min-w-11 flex-col items-center justify-center rounded-xl text-base font-semibold tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-1',
                            isSelected
                              ? 'bg-gray-900 text-white'
                              : isAvailable
                                ? 'text-gray-900 hover:bg-gray-100'
                                : 'text-gray-300',
                          )}
                        >
                          {dayOfMonth === 1 && (
                            <span className="text-[9px] font-medium uppercase leading-none" aria-hidden="true">
                              {formatMonthShort(day.date)}
                            </span>
                          )}
                          <span aria-hidden="true">{dayOfMonth}</span>
                          {isConfirmed && day.isEarliest && (
                            <span
                              className={clsx(
                                'absolute bottom-1.5 h-1 w-1 rounded-full',
                                isSelected ? 'bg-white' : 'bg-emerald-600',
                              )}
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          <Dialog.Description className="mt-2 flex items-center gap-1.5 px-1 text-xs text-gray-500">
            {isConfirmed ? (
              <>
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" aria-hidden="true" />
                Earliest {formatShortDate(earliestDate)} · Closed weekends
              </>
            ) : (
              'The branch confirms the date · Closed weekends'
            )}
          </Dialog.Description>
        </div>

        <div className="border-t border-gray-100 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
          <Button type="button" className="w-full" onClick={() => onConfirm(pendingDate)}>
            Use {formatShortDate(pendingDate)}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )
}

/**
 * Index to move focus to for a calendar key press (AC8), skipping days that
 * can't be picked. The current index when there's nowhere to go; undefined for
 * keys the calendar doesn't handle.
 */
function nextFocusableDay(days: CalendarDay[], index: number, key: string): number | undefined {
  const isAvailable = (candidate: number) => days[candidate]?.status === 'available'
  const walk = (step: number) => {
    for (let candidate = index + step; candidate >= 0 && candidate < days.length; candidate += step) {
      if (isAvailable(candidate)) return candidate
    }
    return index
  }
  const weekStart = index - (index % 7)
  switch (key) {
    case 'ArrowLeft':
      return walk(-1)
    case 'ArrowRight':
      return walk(1)
    case 'ArrowUp':
      return walk(-7)
    case 'ArrowDown':
      return walk(7)
    case 'Home':
      for (let candidate = weekStart; candidate < index; candidate += 1) {
        if (isAvailable(candidate)) return candidate
      }
      return index
    case 'End':
      for (let candidate = weekStart + 6; candidate > index; candidate -= 1) {
        if (isAvailable(candidate)) return candidate
      }
      return index
    default:
      return undefined
  }
}
